import { useState, useEffect, useCallback, ReactElement, ReactNode } from 'react';
import { doubleArrowIcon } from '../../../assets/images';

export const INITIAL_LEFT_SIDEBAR_WIDTH = 242;
export const MAX_LEFT_SIDEBAR_WIDTH = 400;

interface SecondLeftSidebarProps {
    component: ((ReactElement<any, any> & ReactNode) & ReactNode & JSX.Element) | null;
    setLeftSidebarWidth?: (width: number) => void;
    isClosed: boolean;
    setIsClosed: (isClosed: boolean) => void;
}

const SecondLeftSidebar = (props: SecondLeftSidebarProps) => {
    const { component, setLeftSidebarWidth, isClosed, setIsClosed } = props;
    const [sidebarWidth, setSidebarWidth] = useState(INITIAL_LEFT_SIDEBAR_WIDTH);
    const [isResizing, setIsResizing] = useState(false);
    const [isHidden, setIsHidden] = useState(false);

    const startResizing = useCallback((_mouseDownEvent: any) => {
        setIsResizing(true);
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
    }, []);

    // Resize function for changing the width of sidebar
    const resize = useCallback(
        (mouseMoveEvent: any) => {
            if (isResizing) {
                const toolbar = document.querySelector<HTMLElement>('.page-toolbar');

                const currentPosition = mouseMoveEvent.clientX - 49;

                // conditions to make sure doesn't resizing doesn't pass max width and min width
                if (currentPosition < MAX_LEFT_SIDEBAR_WIDTH && currentPosition > 150) {
                    toolbar && toolbar!.style.setProperty('width', `${currentPosition}px`);
                    setSidebarWidth(currentPosition);
                    setLeftSidebarWidth && setLeftSidebarWidth(currentPosition);
                } else if (currentPosition >= MAX_LEFT_SIDEBAR_WIDTH) {
                    setSidebarWidth(MAX_LEFT_SIDEBAR_WIDTH);
                    setLeftSidebarWidth && setLeftSidebarWidth(MAX_LEFT_SIDEBAR_WIDTH);
                    toolbar && toolbar!.style.setProperty('width', `700px`);
                } else if (currentPosition <= 250) {
                    setSidebarWidth(250);
                    setLeftSidebarWidth && setLeftSidebarWidth(250);
                    toolbar && toolbar!.style.setProperty('width', `250px`);
                }
            }
        },
        [isResizing]
    );

    useEffect(() => {
        window.addEventListener('mousemove', resize);
        window.addEventListener('mouseup', stopResizing);
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [resize, stopResizing]);

    useEffect(() => {
        if (!isClosed) {
            const toolbar = document.querySelector<HTMLElement>('.page-toolbar');
            toolbar && toolbar!.style.setProperty('width', `${INITIAL_LEFT_SIDEBAR_WIDTH}px`);
            setSidebarWidth(INITIAL_LEFT_SIDEBAR_WIDTH);
            setLeftSidebarWidth && setLeftSidebarWidth(INITIAL_LEFT_SIDEBAR_WIDTH);
            setTimeout(() => setIsHidden(false), 500);
        }
    }, [isClosed]);

    return (
        <div
            id="second-left-side-bar"
            className="second-left-side-bar"
            style={{ width: sidebarWidth, display: isHidden ? 'none' : 'flex' }}
        >
            <div data-testid="resizer" className="sidebar-resizer" onMouseDown={startResizing} />
            {component}

            <div className="toggle-left-side-bar-close-button-container">
                {!isClosed && (
                    <img
                        className="toggle-left-side-bar-icon close"
                        src={doubleArrowIcon}
                        alt="close-left-side-bar-icon"
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const toolbar = document.querySelector<HTMLElement>('.page-toolbar');
                            toolbar && toolbar!.style.setProperty('width', '0px');
                            setSidebarWidth(0);
                            setLeftSidebarWidth && setLeftSidebarWidth(0);
                            setIsClosed(true);

                            setTimeout(() => setIsHidden(true), 500);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SecondLeftSidebar;
